import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { UPDATE_QUIZ_ASSIGNMENT } from '../../Reducers/Actions';
import { SnackBarContext } from '../../Context/SnackBarContextProvider';
import { useUpdateQuizAssignmentMutation } from '../../services/SkillsManagement';
import EditIcon from '@mui/icons-material/Edit';
import { MenuItem, ListItemIcon, ListItemText, Grid, TextField } from '@mui/material';
import MuiDialog from '../../SystemDesign/MuiDialog';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FormInput from '../../SystemDesign/FormInput';

// Helpers
const toLocalDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = d.getFullYear();
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return '';
  }
};

const toIsoOrNull = (val) => {
  if (!val) return undefined;
  const d = new Date(val);
  if (isNaN(d.getTime())) return undefined;
  return d.toISOString();
};

// Component
const UpdateQuizAssignment = ({ row }) => {
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
  const [dialogOpen, setDialogOpen] = useState(false);

  const [updateAssignment, { isLoading }] = useUpdateQuizAssignmentMutation();

  const assignment = row?.original || {};
  const id = assignment.assignmentId ?? assignment.id;
  const isPending = (assignment.status || 'pending') === 'pending';

  // Initial form values
  const initialValues = {
    dueAt: toLocalDate(assignment.dueAt),
    expiresAt: toLocalDate(assignment.expiresAt),
    note: assignment.note || '',
  };


  // Grid sizing similar to CreateAssignement.getFieldGridProps
  const getFieldGridProps = (name) => {
    switch (name) {
      case 'dueAt':
      case 'expiresAt':
        return { xs: 12, sm: 6, md: 6, lg: 6 };
      case 'note':
        return { xs: 12, sm: 12, md: 12, lg: 12 };
      default:
        return { xs: 12, sm: 6, md: 6 };
    }
  };

  // Validation
  const validationSchema = Yup.object({
    note: Yup.string().max(500, 'Note is too long'),
    dueAt: Yup.date().nullable().typeError('Invalid date'),
    expiresAt: Yup.date()
      .nullable()
      .typeError('Invalid date')
      .when('dueAt', (dueAt, schema) =>
        dueAt ? schema.min(dueAt, 'Expires at must be after Due at') : schema
      ),
  });

  const handleUpdate = async (values) => {
    try {
      if (!id) throw new Error('No assignment ID found');
      if (!isPending) throw new Error('Only pending assignments can be updated');

      const body = {
        ...(values.note ? { note: values.note } : {}),
        ...(values.dueAt ? { dueAt: toIsoOrNull(values.dueAt) } : {}),
        ...(values.expiresAt ? { expiresAt: toIsoOrNull(values.expiresAt) } : {}),
      };

      const result = await updateAssignment({
        assignmentId: Number(id),
        body,
      }).unwrap();

      const updated = result?.data || result;

      dispatch({ type: UPDATE_QUIZ_ASSIGNMENT, payload: updated });

      setInfoAlert({
        severity: 'success',
        message: 'Assignment successfully updated.',
      });
      setOpenSnackBar(true);

      setDialogOpen(false);
      // Notify assignment lists to refresh immediately
      try { window.dispatchEvent(new CustomEvent('quiz-assignments:changed')); } catch {}
      return true;
    } catch (error) {
      console.error('Failed to update quiz assignment:', error);
      setInfoAlert({
        severity: 'error',
        message: error?.data?.message || error?.message || 'Failed to update assignment',
      });
      setOpenSnackBar(true);
      return false;
    }
  };

  const triggerButton = (
    <MenuItem
      onClick={() => {
        if (isPending) setDialogOpen(true);
      }}
      disabled={!isPending}
      title={isPending ? 'Edit assignment' : 'Only pending assignments can be edited'}
    >
      <ListItemIcon>
        <EditIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText>Edit</ListItemText>
    </MenuItem>
  );

  const dialogContent = (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
      onSubmit={handleUpdate}
    >
      {(formik) => (
        <Form id="updateQuizAssignmentForm">
          <Grid container spacing={2} marginTop={1}>
            <Grid item size={{ ...getFieldGridProps('dueAt') }}>
              <FormInput
                name="dueAt"
                label="Due Date (optional)"
                type="date"
              />
            </Grid>

            <Grid item size={{ ...getFieldGridProps('expiresAt') }}>
              <FormInput
                name="expiresAt"
                label="Expiration Date (optional)"
                type="date"
              />
            </Grid>

            <Grid item size={{ ...getFieldGridProps('note') }}>
              <TextField
                fullWidth
                label="Note"
                name="note"
                value={formik.values.note}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.note && Boolean(formik.errors.note)}
                helperText={(formik.touched.note && formik.errors.note) || ' '}
                placeholder="Optional note..."
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );

  return (
    <>
      {triggerButton}

      <MuiDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        icon={<EditIcon />}
        title="Update Quiz Assignment"
        subtitle={`Update assignment ${id || ''} (pending only)`}
        content={dialogContent}
        existAction={true}
        actionTitle="Update Assignment"
        actionType="submit"
        formId="updateQuizAssignmentForm"
        isLoading={isLoading}
      />
    </>
  );
};

// Optional: static properties for menu integrations
UpdateQuizAssignment.menuIcon = <EditIcon fontSize="small" />;
UpdateQuizAssignment.menuLabel = 'Edit';

export default UpdateQuizAssignment;